import React, { memo } from 'react';
import { Box, Button, Card, CardContent, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import { TripSummaryDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import TripMiniMap from './trip-mini-map';
import TripScoreBadge from './trip-score-badge';
import { formatTripTime, formatDuration, formatDistance, normalizeScore } from './trip-utils';

/**
 * Props for the TripCard component.
 */
export interface TripCardProps {
	/** Trip summary data from the telematics API. */
	trip: TripSummaryDto;
	/** Callback fired when the "View score breakdown" / "View trip" button is clicked. */
	onViewScoreBreakdown?: (tripId: string) => void;
	/**
	 * Whether to show the score badge and score wording. Read once by the page
	 * and passed down so a long list does not subscribe to the flag per card.
	 * Default: false. Scores are hidden unless the parent explicitly enables them.
	 */
	showDriverScore?: boolean;
}

/**
 * Card component displaying a trip summary with map preview and details.
 *
 * Shows a mini map with the trip route (if polyline available), trip metadata
 * (time, duration, distance), start/end addresses, score badge, and a button
 * to navigate to the detailed score breakdown view.
 *
 * @component
 * @example
 * ```tsx
 * <TripCard
 *   trip={tripData}
 *   onViewScoreBreakdown={(tripId) => navigate(`/trips/${tripId}`)}
 * />
 * ```
 * Memoised: re-renders only when `trip`, `onViewScoreBreakdown` or `showDriverScore` change.
 */
const TripCard: React.FC<TripCardProps> = memo(function TripCard({
	trip,
	onViewScoreBreakdown,
	showDriverScore = false,
}: TripCardProps) {
	const theme = useTheme();

	const handleViewScoreBreakdown = () => {
		if (onViewScoreBreakdown && trip.tripId) {
			onViewScoreBreakdown(trip.tripId);
		}
	};

	const timeString = trip.startTimeUtc ? formatTripTime(new Date(trip.startTimeUtc)) : '';
	const durationString = formatDuration(trip.durationSeconds ?? 0);
	const distanceString = formatDistance(trip.distanceMiles ?? 0);

	return (
		<Card
			elevation={0}
			sx={{
				backgroundColor: theme.palette.grey[50],
				borderRadius: 3,
				overflow: 'hidden',
			}}
		>
			{/* Map Container with padding */}
			{trip.polyline && (
				<Box sx={{ p: 2, pb: 0 }}>
					<Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
						<TripMiniMap polyline={trip.polyline} height={260} />
					</Box>
				</Box>
			)}

			<CardContent sx={{ p: 2, pt: 2 }}>
				{/* Time, Duration, Distance, Score Row */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 1.5,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
						<DirectionsCarOutlinedIcon sx={{ fontSize: 22, color: theme.palette.text.primary }} />
						<Typography variant='body1' fontWeight='bold'>
							{timeString}
						</Typography>
						<Typography variant='body2' component='span' fontWeight={500}>
							({durationString})
						</Typography>
						<Typography variant='caption' component='span' fontWeight='bold'>
							• {distanceString}
						</Typography>
					</Box>

					{showDriverScore && trip.totalScore !== undefined && (
						<TripScoreBadge score={normalizeScore(trip.totalScore)} />
					)}
				</Box>

				{/* Addresses - indented to align with text after car icon */}
				<Box sx={{ display: 'flex', mb: 2.5, pl: 3.5 }}>
					{/* Icons column with dotted line */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							mr: 1,
						}}
					>
						<GpsFixedIcon
							sx={{
								fontSize: 18,
								color: theme.palette.text.primary,
							}}
						/>
						{/* Dotted line connecting icons */}
						<Box
							sx={{
								width: 0,
								height: 16,
								borderLeft: `2px dashed ${theme.palette.grey[400]}`,
								my: 0.5,
							}}
						/>
						<LocationOnOutlinedIcon
							sx={{
								fontSize: 18,
								color: theme.palette.text.primary,
							}}
						/>
					</Box>

					{/* Address text column */}
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 0.25 }}>
						{trip.startAddress && (
							<Typography variant='body2' color='text.primary'>
								{trip.startAddress}
							</Typography>
						)}
						{trip.endAddress && (
							<Typography variant='body2' color='text.primary' sx={{ mt: 'auto' }}>
								{trip.endAddress}
							</Typography>
						)}
					</Box>
				</Box>

				{/* View Score Breakdown Button */}
				<Button
					variant='outlined'
					fullWidth
					onClick={handleViewScoreBreakdown}
					sx={{
						borderColor: theme.palette.primary.main,
						color: theme.palette.primary.main,
						':hover': {
							backgroundColor: alpha(theme.palette.primary.main, 0.1),
						},
						cursor: 'pointer',
						textTransform: 'none',
						py: 1.25,
						borderRadius: 1.5,
					}}
				>
					{showDriverScore ? 'View score breakdown' : 'View trip'}
				</Button>
			</CardContent>
		</Card>
	);
});

export default TripCard;
